import {z} from 'zod';

import {isNil} from 'ramda';

import i18n from '@dms/i18n';

import {MaterialDiscountOption} from './materialDiscountOption';
import {WorkDiscountOption} from './workDiscountOption';

export const discountSectionSchema = z
  .object({
    isWorkExpanded: z.boolean(),
    selectedWorkOption: z.nativeEnum(WorkDiscountOption).nullable(),
    isMaterialExpanded: z.boolean(),
    selectedMaterialOption: z.nativeEnum(MaterialDiscountOption).nullable(),
    isSmallUnitMaterialExpanded: z.boolean(),
    isFixedValueWithVat: z.boolean(),
    work: z
      .object({
        percentDiscount: z
          .object({
            discountRate: z.number().min(0).max(100).optional(),
          })
          .optional(),
        discountedWorkRates: z
          .object({
            discountedWorkRate: z
              .array(
                z.object({
                  workRateType: z.string().optional(),
                  workRatePriceWithoutVat: z.number().min(0).optional(),
                })
              )
              .optional(),
          })
          .optional(),
      })
      .optional(),
    material: z
      .object({
        percentDiscount: z
          .object({
            discountOnSalePriceRate: z.number().min(0).max(100).optional(),
            discountMarginToPurchasePriceRate: z.number().min(0).max(100).optional(),
          })
          .optional(),
      })
      .optional(),
    smallUnitMaterial: z
      .object({
        fixedValueAmountWithoutVat: z.number().min(0).nullable().optional(),
        fixedValueAmountWithVat: z.number().min(0).nullable().optional(),
      })
      .optional(),
  })
  .superRefine((data, ctx) => {
    const isPercentDiscountRequired =
      data.selectedWorkOption === WorkDiscountOption.PercentageDiscount && data.isWorkExpanded;
    const isDiscountedWorkRateRequired =
      data.selectedWorkOption === WorkDiscountOption.DiscountedWorkRate && data.isWorkExpanded;
    const isDiscountOnSalePriceRequired =
      data.selectedMaterialOption === MaterialDiscountOption.DiscountOnSalePrice &&
      data.isMaterialExpanded;
    const isMarginToPurchasePriceRequired =
      data.selectedMaterialOption === MaterialDiscountOption.MarginToPurchasePrice &&
      data.isMaterialExpanded;

    if (isPercentDiscountRequired && data.work?.percentDiscount?.discountRate === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['work', 'percentDiscount', 'discountRate'],
        message: i18n.t('general.errors.mixed.required'),
      });
    }

    if (isDiscountedWorkRateRequired) {
      data.work?.discountedWorkRates?.discountedWorkRate?.forEach((rate, index) => {
        if (!rate.workRateType) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['work', 'discountedWorkRates', 'discountedWorkRate', index, 'workRateType'],
            message: i18n.t('general.errors.mixed.required'),
          });
        }

        if (rate.workRatePriceWithoutVat === undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [
              'work',
              'discountedWorkRates',
              'discountedWorkRate',
              index,
              'workRatePriceWithoutVat',
            ],
            message: i18n.t('general.errors.mixed.required'),
          });
        }
      });
    }

    if (
      isDiscountOnSalePriceRequired &&
      data.material?.percentDiscount?.discountOnSalePriceRate === undefined
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['material', 'percentDiscount', 'discountOnSalePriceRate'],
        message: i18n.t('general.errors.mixed.required'),
      });
    }

    if (
      isMarginToPurchasePriceRequired &&
      data.material?.percentDiscount?.discountMarginToPurchasePriceRate === undefined
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['material', 'percentDiscount', 'discountMarginToPurchasePriceRate'],
        message: i18n.t('general.errors.mixed.required'),
      });
    }

    if (
      data.isSmallUnitMaterialExpanded &&
      !data.isFixedValueWithVat &&
      isNil(data.smallUnitMaterial?.fixedValueAmountWithoutVat)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['smallUnitMaterial', 'fixedValueAmountWithoutVat'],
        message: i18n.t('general.errors.mixed.required'),
      });
    }

    if (
      data.isSmallUnitMaterialExpanded &&
      data.isFixedValueWithVat &&
      isNil(data.smallUnitMaterial?.fixedValueAmountWithVat)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['smallUnitMaterial', 'fixedValueAmountWithVat'],
        message: i18n.t('general.errors.mixed.required'),
      });
    }
  });

export type DiscountSectionFormType = z.infer<typeof discountSectionSchema>;
